/**
 * Auth seam (local-dev-environment Phase 2). Mode is env-driven via
 * `VITE_AUTH_MODE` ('local' | anything else → 'cognito', the default).
 *
 * In `cognito` mode every function below delegates to the existing
 * `aws-amplify/auth` calls unchanged (zero regression — this is exactly
 * what `frontend/src/auth/AuthContext.tsx` already calls directly).
 *
 * In `local` mode, `signIn` posts credentials to `POST /__auth/login`
 * (served by `localdev/server.ts`) and stores the returned JWT in
 * `localStorage`, matching the shape Amplify's `fetchAuthSession()` exposes
 * (`{ tokens: { idToken: { payload } } }`) so a future AuthContext wiring
 * can swap this seam in without changing its own logic.
 */
import {
  signIn as amplifySignIn,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession as amplifyFetchAuthSession,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';

export type AuthMode = 'cognito' | 'local';

const LOCAL_TOKEN_KEY = 'banyan_local_auth_token';

/** Reads VITE_AUTH_MODE. Anything other than the literal 'local' is 'cognito'. */
export function getAuthMode(): AuthMode {
  const raw = (import.meta.env?.VITE_AUTH_MODE ?? '').toString().toLowerCase();
  return raw === 'local' ? 'local' : 'cognito';
}

export interface SignInInput {
  username: string;
  password: string;
  options?: unknown;
}

export interface AuthSession {
  tokens?: {
    idToken?: {
      payload: Record<string, unknown>;
      /**
       * Returns the raw encoded JWT — `api/client.ts`'s `getAuthToken()`
       * calls `idToken.toString()` to build the `Authorization: Bearer`
       * header. Amplify's real `idToken` implements this already; the
       * local-mode stub below wires it explicitly to the stored token.
       */
      toString(): string;
    };
  };
}

export interface CurrentUser {
  username: string;
}

/**
 * Loose result shape covering both Amplify's real `SignInOutput` (cognito
 * mode) and the local-mode stub — callers (AuthContext.tsx) only ever read
 * `nextStep?.signInStep`.
 */
export interface SignInResult {
  isSignedIn?: boolean;
  nextStep?: { signInStep?: string };
  [key: string]: unknown;
}

export interface AuthClient {
  signIn(input: SignInInput): Promise<SignInResult>;
  getCurrentUser(): Promise<CurrentUser>;
  fetchAuthSession(opts?: { forceRefresh?: boolean }): Promise<AuthSession>;
  signOut(): Promise<void>;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('authClient: malformed local token');
  }
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = atob(base64);
  return JSON.parse(json) as Record<string, unknown>;
}

async function localSignIn(input: SignInInput): Promise<SignInResult> {
  const res = await fetch('/__auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: input.username, password: input.password }),
  });
  if (!res.ok) {
    throw new Error(`authClient: local sign-in failed (status ${res.status})`);
  }
  const data = (await res.json()) as { token: string };
  localStorage.setItem(LOCAL_TOKEN_KEY, data.token);
  return { isSignedIn: true, nextStep: { signInStep: 'DONE' } };
}

async function localGetCurrentUser(): Promise<CurrentUser> {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);
  if (!token) {
    throw new Error('authClient: no local session');
  }
  const claims = decodeJwtPayload(token);
  return { username: String(claims.sub ?? '') };
}

async function localFetchAuthSession(): Promise<AuthSession> {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);
  if (!token) return { tokens: undefined };
  const claims = decodeJwtPayload(token);
  return { tokens: { idToken: { payload: claims, toString: () => token } } };
}

async function localSignOut(): Promise<void> {
  localStorage.removeItem(LOCAL_TOKEN_KEY);
}

const cognitoClient: AuthClient = {
  signIn: (input) =>
    amplifySignIn(input as Parameters<typeof amplifySignIn>[0]) as Promise<SignInResult>,
  getCurrentUser: () => amplifyGetCurrentUser(),
  fetchAuthSession: (opts) => amplifyFetchAuthSession(opts),
  signOut: () => amplifySignOut(),
};

const localClient: AuthClient = {
  signIn: localSignIn,
  getCurrentUser: localGetCurrentUser,
  fetchAuthSession: localFetchAuthSession,
  signOut: localSignOut,
};

/**
 * The active auth client for the current mode. Resolved once at module
 * load (mirrors `getAuthMode()` — a build has a single, static
 * `VITE_AUTH_MODE`; tests re-import this module after `vi.stubEnv` to pick
 * up a different mode).
 */
export const authClient: AuthClient = getAuthMode() === 'local' ? localClient : cognitoClient;
